import React, { useMemo } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListSubheader,
  Typography,
  Divider,
} from '@mui/material';
import { Label as LabelIcon } from '@mui/icons-material';

const GROUP_ORDER = ['Client', 'User', 'Firm', 'Other'];

function normalizeGroup(tag) {
  const g = tag.group || tag.category || tag.section;
  if (typeof g === 'string') {
    const lower = g.toLowerCase();
    if (lower.includes('client')) return 'Client';
    if (lower.includes('user')) return 'User';
    if (lower.includes('firm')) return 'Firm';
    return g.charAt(0).toUpperCase() + g.slice(1);
  }
  const s = (tag.tag || tag.value || '').toLowerCase();
  if (/\{\{\s*client/i.test(s)) return 'Client';
  if (/\{\{\s*user/i.test(s)) return 'User';
  if (/\{\{\s*firm/i.test(s)) return 'Firm';
  return 'Other';
}

export default function MergeTagInserter({ anchorEl, open, onClose, mergeTags, onInsert }) {
  const grouped = useMemo(() => {
    const buckets = { Client: [], User: [], Firm: [], Other: [] };
    (mergeTags || []).forEach((mt) => {
      const g = normalizeGroup(mt);
      const key = buckets[g] !== undefined ? g : 'Other';
      buckets[key].push(mt);
    });
    return buckets;
  }, [mergeTags]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { maxHeight: 360, minWidth: 260 } } }}
    >
      <ListSubheader disableSticky sx={{ lineHeight: 1.5, py: 1 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          Insert merge tag
        </Typography>
      </ListSubheader>
      {GROUP_ORDER.map((group) => {
        const items = grouped[group];
        if (!items?.length) return null;
        return (
          <React.Fragment key={group}>
            <ListSubheader disableSticky sx={{ bgcolor: 'background.default', lineHeight: 1.5, py: 0.5 }}>
              <Typography variant="caption" fontWeight={600}>
                {group}
              </Typography>
            </ListSubheader>
            {items.map((mt) => {
              const tagStr = mt.tag || mt.value || '';
              const label = mt.label || mt.name || tagStr;
              return (
                <MenuItem
                  key={`${group}-${tagStr}`}
                  dense
                  onClick={() => {
                    onInsert(tagStr);
                    onClose();
                  }}
                >
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {label}
                  </Typography>
                </MenuItem>
              );
            })}
            <Divider />
          </React.Fragment>
        );
      })}
    </Menu>
  );
}

export function MergeTagButton({ onClick, disabled }) {
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<LabelIcon sx={{ fontSize: 18 }} />}
      onClick={onClick}
      disabled={disabled}
      sx={{ textTransform: 'none', flexShrink: 0 }}
    >
      Insert merge tag
    </Button>
  );
}
